import { useEffect, useRef, useState } from "react";
import "./Agent.css";
import orbitSvg from "./../orbit.svg";
import MessageTransformer from "./UI/MessageTransformer";
import ChatInput from "./UI/ChatInput";
import { ArrowDown } from "lucide-react";
import useToolCalls from "../hooks/useToolCalls";

const Agent = () => {
  const [input, setInput] = useState("");
  const [isScrolLatest, setIsScrollLatest] = useState(false);
  const chatContainerRef = useRef(null);

  const {
    messages,
    chat,
    isFetching,
    fetchChatResponse,
    setMessages,
    setChat,
  } = useToolCalls();

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [input, isFetching]);

  useEffect(() => {
    const chatContainer = chatContainerRef.current;
    if (!chatContainer) return;

    const handleScroll = () => {
      const isAtBottom =
        chatContainer.scrollHeight - chatContainer.scrollTop ===
        chatContainer.clientHeight;

      setIsScrollLatest(!isAtBottom);
    };

    chatContainer.addEventListener("scroll", handleScroll);
    return () => chatContainer.removeEventListener("scroll", handleScroll);
  }, []);

  const handleClickScrollLatest = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
    setIsScrollLatest(false);
  };

  const handleUserQuery = async () => {
    if (!input.trim() || isFetching) return;

    const userMessage = { role: "user", content: input };
    setInput("");
    setMessages([...messages, userMessage]);

    const conversationHistory = [...messages, userMessage];
    const tempChat = [...chat, userMessage];

    setChat([...tempChat]);
    await fetchChatResponse(conversationHistory, tempChat);
  };

  const onSubmitForm = async (userInput) => {
    if (!userInput.trim() || isFetching) return;

    const userMessage = { role: "user", content: userInput };
    setInput("");
    setMessages([...messages, userMessage]);

    const conversationHistory = [...messages, userMessage];
    const tempChat = [...chat];
    await fetchChatResponse(conversationHistory, tempChat);
  };

  const onRetry = async () => {
    if (isFetching) return;
    const conversationHistory = [...messages];
    const tempChat = [...chat];
    tempChat.pop();
    await fetchChatResponse(conversationHistory, tempChat);
  };

  return (
    <>
      <div className="chat-container">
        <div className="chat-header">
          <img src={orbitSvg} alt="Orbit" className="orbit-img" />
          <span className="orbit-heading">Orbit</span>
        </div>
        <div className="chat-wrapper">
          <div ref={chatContainerRef} className="chat-messages">
            {chat
              .filter((msg) => msg.role !== "system")
              .map((msg, index) => (
                <MessageTransformer
                  key={index}
                  msg={msg}
                  index={index}
                  onSubmitInputs={onSubmitForm}
                  onRetry={onRetry}
                />
              ))}
            {isFetching && <span className="typing-indicator">typing...</span>}
            {isScrolLatest && (
              <div className="scroll-to-latest-container">
                <div
                  className="scroll-to-latest"
                  onClick={handleClickScrollLatest}
                >
                  <ArrowDown className="arrow-down" />
                  <span className="scroll-to-latest-text">
                    Scroll to latest message
                  </span>
                </div>
              </div>
            )}
          </div>
          <ChatInput
            handleQuery={handleUserQuery}
            input={input}
            isFetching={isFetching}
            setInput={setInput}
            placeholder="What can I do for you..."
          />
        </div>
      </div>
    </>
  );
};

export default Agent;
